import * as React from 'react';

import SectionHeader from '@/components/display/headers/SectionHeader';
import ImageWithFallback from '@core/components/display/images/ImageWithFallback';
import { EventSpeaker } from '@/types/EventSpeaker';
import { classNames } from '@core/utils/functions/class-names';
import Link from 'next/link';

type SectionSpeakersProps = {
    className?: string;
    eventSlug: string;
    highlightedSpeaker?: EventSpeaker | null;
    speakers: EventSpeaker[];
    accentColor: string;
    displayCount?: number;
    showAllSpeakersLink: boolean;
};

const SectionSpeakers = (props: SectionSpeakersProps) => {
    const { className, speakers, accentColor, highlightedSpeaker, eventSlug, displayCount, showAllSpeakersLink } =
        props;

    if (!speakers || speakers.length === 0) {
        return <></>;
    }

    return (
        <section className={classNames('flex flex-col xs:items-center xs:justify-center', className)}>
            <SectionHeader
                className="px-0 py-5"
                title="speakers"
                linkTitle={
                    showAllSpeakersLink ? (
                        <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-2">
                            <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-1">
                                <Link href={`/event/${eventSlug}/speakers`}>
                                    <a
                                        className="flex-grow-0 flex-shrink-0 text-base text-left uppercase"
                                        style={{ color: accentColor }}
                                    >
                                        all speakers
                                    </a>
                                </Link>
                            </div>
                            <svg
                                width={16}
                                height={9}
                                viewBox="0 0 16 9"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="flex-grow-0 flex-shrink-0"
                                preserveAspectRatio="xMidYMid meet"
                            >
                                <path
                                    d="M12.01 3.79688H0V5.79688H12.01V8.79688L16 4.79688L12.01 0.796875V3.79688Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </div>
                    ) : (
                        ''
                    )
                }
                linkUrl={`/events/${eventSlug}/speakers`}
            />
            {speakers.length > 0 && (
                <div className="w-full flex align-left">
                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5 lg:gap-10">
                        {highlightedSpeaker && (
                            <div className="xs:row-span-2 xs:col-span-2">
                                <Link href={`/speaker/${highlightedSpeaker.slug}`} passHref>
                                    <a>
                                        <div className="">
                                            <ImageWithFallback
                                                src={highlightedSpeaker.thumbnail || ''}
                                                width={380}
                                                height={380}
                                                alt=""
                                                className="relative object-fill w-full"
                                            />
                                        </div>
                                        <div className="flex flex-col justify-center items-center self-stretch flex-grow relative gap-1 px-5 py-10 bg-[#000a39]">
                                            <p className="text-base text-center uppercase text-[#5a7aed]">keynote</p>
                                            <p className="text-2xl text-center capitalize text-white">
                                                {highlightedSpeaker.title}
                                            </p>
                                            <p className="text-lg text-center text-[#e0e3eb]">
                                                {highlightedSpeaker.custom_fields?.speaker_job?.value} |{' '}
                                                {highlightedSpeaker.custom_fields?.speaker_company?.value}
                                            </p>
                                        </div>
                                    </a>
                                </Link>
                            </div>
                        )}

                        {speakers.slice(1, displayCount || speakers.length).map(speaker => (
                            <div className="flex flex-col gap-3 bg-white w-full xs:w-48 md:w-32" key={speaker.id}>
                                <Link href={`/speaker/${speaker.slug}`} passHref>
                                    <a>
                                        <div className="w-full">
                                            <ImageWithFallback
                                                src={speaker.thumbnail || ''}
                                                width={155}
                                                height={155}
                                                alt=""
                                                className="relative w-full object-fill"
                                            />
                                        </div>
                                        <div className="w-full flex flex-col justify-center items-center">
                                            <p className="text-xl text-center text-[#000a39]">{speaker.title}</p>
                                            <p className="text-xs text-center text-[#254cda]">
                                                {speaker.custom_fields?.speaker_company?.value}
                                            </p>
                                            <p className="text-xs text-center text-[#979797]">
                                                {speaker.custom_fields?.speaker_job?.value}
                                            </p>
                                        </div>
                                    </a>
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

export default SectionSpeakers;
